import React, { useState, useEffect, useRef } from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { X, Send, Bot, Loader2, Minimize2 } from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const SurveyAIOverlay = () => {
    const { isOverlayOpen, activeInstanceId, isFullScreen, closeSurvey } = useSurvey();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [surveyStatus, setSurveyStatus] = useState('loading'); // loading, active, completed, error
    const [surveyTitle, setSurveyTitle] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        // Focus input when overlay opens
        if (isOverlayOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOverlayOpen]);

    useEffect(() => {
        let cancelled = false;

        if (isOverlayOpen && activeInstanceId) {
            const initializeSurvey = async () => {
                try {
                    setSurveyStatus('loading');
                    const res = await api.post(`/surveys/instance/${activeInstanceId}/start`);
                    if (cancelled) return;

                    const { instance, nextQuestion, totalQuestions } = res.data;

                    // Store survey title
                    setSurveyTitle(res.data.instance.survey_title || 'Survey');
                    setCurrentQuestion(nextQuestion);
                    // Enable input as soon as we have the question
                    setSurveyStatus('active');

                    // Get AI-generated welcome message
                    try {
                        const aiRes = await api.post(`/surveys/instance/${activeInstanceId}/ai-response`, {
                            conversationHistory: [],
                            currentQuestionId: nextQuestion?.id,
                            isFirstQuestion: true,
                            isLastQuestion: false
                        });
                        if (cancelled) return;

                        setMessages([
                            { role: 'assistant', content: aiRes.data.response },
                        ]);
                    } catch (aiError) {
                        if (cancelled) return;
                        console.error("Failed to get AI welcome message", aiError);
                        // Fallback: show a simple welcome message, but keep survey active
                        let fallbackMsg = "Welcome to this survey! Let's get started.";
                        if (nextQuestion) {
                            fallbackMsg += `\n\n${nextQuestion.question_text}`;
                        }
                        setMessages([{ role: 'assistant', content: fallbackMsg }]);
                    }
                } catch (error) {
                    if (cancelled) return;
                    console.error("Failed to start survey", error);
                    setMessages([{ role: 'assistant', content: "I'm having trouble loading this survey. Please try again later." }]);
                    setSurveyStatus('error');
                }
            };
            initializeSurvey();
        } else {
            // Reset state on close
            setMessages([]);
            setCurrentQuestion(null);
            setSurveyStatus('loading');
        }

        return () => { cancelled = true; };
    }, [isOverlayOpen, activeInstanceId]);

    const askQuestion = async (question, previousMessages) => {
        setCurrentQuestion(question);
        setIsTyping(true);

        try {
            // Get total questions for context
            const startRes = await api.post(`/surveys/instance/${activeInstanceId}/start`);
            const { totalQuestions } = startRes.data;

            const isLast = (startRes.data.instance.current_question_index + 1) >= totalQuestions;

            // Get AI-generated question with acknowledgment
            const aiRes = await api.post(`/surveys/instance/${activeInstanceId}/ai-response`, {
                conversationHistory: previousMessages,
                currentQuestionId: question.id,
                isFirstQuestion: false,
                isLastQuestion: isLast
            });

            setMessages(prev => [...prev, { role: 'assistant', content: aiRes.data.response }]);
            setIsTyping(false);
        } catch (error) {
            console.error("Error getting AI response:", error);
            // Fallback to simple question display
            let questionText = question.question_text;
            if (question.question_type === 'rating') questionText += " (Reply with a number 1-5)";
            if (question.question_type === 'boolean') questionText += " (Reply Yes/No)";
            setMessages(prev => [...prev, { role: 'assistant', content: questionText }]);
            setIsTyping(false);
        }
    };

    const handleSend = async () => {
        if (!input.trim() || isTyping || !currentQuestion) return;

        const answerText = input.trim();
        const skipped = answerText === '/skip'; // Simple skip command

        // Optimistic UI update
        const userMsg = { role: 'user', content: answerText };
        const updatedMessages = [...messages, userMsg];
        setMessages(updatedMessages);
        setInput('');
        setIsTyping(true);

        try {
            // Send to backend
            const res = await api.post(`/surveys/instance/${activeInstanceId}/response`, {
                questionId: currentQuestion.id,
                responseText: answerText,
                skipped: skipped
            });

            const { status, nextIndex, total } = res.data;

            // Check if this was the last question
            if (status === 'completed') {
                // Get AI-generated closing message
                const aiRes = await api.post(`/surveys/instance/${activeInstanceId}/ai-response`, {
                    conversationHistory: updatedMessages,
                    currentQuestionId: null,
                    isFirstQuestion: false,
                    isLastQuestion: true
                });

                setMessages(prev => [...prev, { role: 'assistant', content: aiRes.data.response }]);
                setIsTyping(false);
                setSurveyStatus('completed');

                setTimeout(() => {
                    closeSurvey();
                    toast.success("Survey Completed");
                }, 4000);
            } else {
                // Fetch next question
                const nextRes = await api.post(`/surveys/instance/${activeInstanceId}/start`);
                if (nextRes.data.nextQuestion) {
                    await askQuestion(nextRes.data.nextQuestion, updatedMessages);
                } else {
                    finishSurvey();
                }
            }

        } catch (error) {
            console.error("Error submitting answer", error);
            setIsTyping(false);
            toast.error("Failed to send answer. Please try again.");
        }
    };

    const finishSurvey = async (previousMessages) => {
        setIsTyping(true);
        try {
            // Get AI-generated closing message
            const aiRes = await api.post(`/surveys/instance/${activeInstanceId}/ai-response`, {
                conversationHistory: previousMessages || messages,
                currentQuestionId: null,
                isFirstQuestion: false,
                isLastQuestion: true
            });

            setMessages(prev => [...prev, { role: 'assistant', content: aiRes.data.response }]);
            setIsTyping(false);
            setSurveyStatus('completed');

            setTimeout(() => {
                closeSurvey();
                toast.success("Survey Completed");
            }, 4000);
        } catch (error) {
            console.error("Error finishing survey:", error);
            setMessages(prev => [...prev, { role: 'assistant', content: "Thank you for completing this survey! Your feedback is incredibly valuable to us." }]);
            setIsTyping(false);
            setSurveyStatus('completed');
            setTimeout(() => {
                closeSurvey();
                toast.success("Survey Completed");
            }, 4000);
        }
    };

    if (!isOverlayOpen) return null;

    // Full-screen mode
    if (isFullScreen) {
        return (
            <div className="fixed inset-0 bg-gray-50 z-[60] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex justify-between items-center text-white shadow-lg">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 rounded-lg">
                            <Bot size={24} className="text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">{surveyTitle || 'Survey'}</h3>
                            <span className="text-sm opacity-90">AI-Powered Survey Experience</span>
                        </div>
                    </div>
                    <button
                        onClick={closeSurvey}
                        className="hover:bg-white/20 p-2 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Messages Container */}
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%] px-5 py-4 rounded-2xl shadow-sm ${msg.role === 'user'
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                                    }`}>
                                    <p className="whitespace-pre-wrap text-base leading-relaxed">{msg.content}</p>
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-5 py-4 shadow-sm">
                                    <Loader2 size={20} className="animate-spin text-blue-500" />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                {/* Input Area */}
                <div className="bg-white border-t border-gray-200 px-6 py-4 shadow-lg">
                    <div className="max-w-4xl mx-auto">
                        <div className="relative flex items-center gap-3">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder={currentQuestion ? "Type your answer..." : "Please wait..."}
                                disabled={!currentQuestion || isTyping || surveyStatus !== 'active'}
                                className="flex-1 bg-gray-100 text-gray-800 text-base rounded-xl px-5 py-4 border-none focus:ring-2 focus:ring-blue-500/30 focus:bg-white transition-all outline-none"
                            />
                            <button
                                onClick={handleSend}
                                disabled={!input.trim() || isTyping || surveyStatus !== 'active'}
                                className="px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all flex items-center gap-2 font-medium"
                            >
                                <Send size={18} />
                                Send
                            </button>
                        </div>
                        {activeInstanceId && (
                            <div className="mt-3 text-center">
                                <span className="text-xs text-gray-500">Type <strong>/skip</strong> to skip a question if allowed</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Bottom-right popup mode (legacy)
    return (
        <div className="fixed bottom-6 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden z-[60] animate-slideInUp">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 flex justify-between items-center text-white">
                <div className="flex items-center gap-2">
                    <div className="bg-white/20 p-1.5 rounded-lg">
                        <Bot size={20} className="text-white" />
                    </div>
                    <div>
                        <h3 className="font-bold text-sm">AI Assistant</h3>
                        <span className="text-[10px] opacity-80 block">Survey Mode</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={closeSurvey} className="hover:bg-white/20 p-1.5 rounded-lg transition-colors">
                        <Minimize2 size={16} />
                    </button>
                    <button onClick={closeSurvey} className="hover:bg-white/20 p-1.5 rounded-lg transition-colors">
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 h-96 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${msg.role === 'user'
                            ? 'bg-blue-600 text-white rounded-br-none'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                            }`}>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                    </div>
                ))}

                {isTyping && (
                    <div className="flex justify-start">
                        <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 bg-white border-t border-gray-100">
                <div className="relative flex items-center gap-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder={currentQuestion ? "Type your answer..." : "Please wait..."}
                        disabled={!currentQuestion || isTyping || surveyStatus !== 'active'}
                        className="flex-1 bg-gray-100/50 text-gray-800 text-sm rounded-xl px-4 py-3 border-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all outline-none"
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isTyping || surveyStatus !== 'active'}
                        className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                    >
                        <Send size={16} />
                    </button>
                </div>
                {activeInstanceId && (
                    <div className="mt-2 text-center">
                        <span className="text-[10px] text-gray-400">Type <strong>/skip</strong> to skip a question if allowed.</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SurveyAIOverlay;
